<template>
  <div class="contract-editor">
    <div class="contract-editor-header">
      <module-header>
        {{ id ? "Редактирование проекта" : "Создание нового проекта" }}
      </module-header>
      <div class="contract-editor-mode">
        <span class="ms-help">Признак создания</span>
        <i-select v-model="mode" @on-change="modeChangeHandler">
          <i-option v-for="item in modes" :value="item.id" :key="item.id">
            {{ item.name }}
          </i-option>
        </i-select>
      </div>
    </div>
    <row :gutter="16">
      <i-col span="7">
        <steps-block
          :steps="filteredSteps"
          :step="step"
          @change-step="changeStep"
        />
      </i-col>
      <i-col span="17">
        <alert v-if="editorState.warning" class="alerts-item" type="warning">
          {{ editorState.warning }}
        </alert>
        <step-container
          :step="step"
          :steps="filteredSteps"
          :contract="contract"
          @change-step="changeStep"
          @complete="saveContract"
        />
        <div class="contract-editor__buttons">
          <i-button
            v-if="isEdited"
            type="default"
            size="default"
            style="margin-right: 16px;"
            @click="clearData"
          >
            Очистить
          </i-button>
          <i-button
            type="success"
            size="default"
            @click="saveContract"
            :loading="saving"
            :disabled="editorState.disabled"
          >
            Создать проект
          </i-button>
        </div>
      </i-col>
    </row>
  </div>
</template>

<script>
import moment from 'moment'
import cloneDeep from 'lodash.clonedeep'
import ModuleHeader from '@/components/parts/module-header'
import StepsBlock from './steps-block'
import StepContainer from './steps/step-container'
import {
  noDocumentsCheckFn,
  guaranteeLetterCheckFn,
  fullCheckFn
} from './utils'
import {
  commonInfo,
  subInfo,
  roadMap,
  documents,
  steps,
  archiveSteps,
  creatingModes,
  allComplexFields
} from '@/config/contracts'
import {
  getDataFromSS,
  saveDataToSS,
  isEditedContract,
  modifyRoadmapsDates,
  modifyComplexData
} from '@/utils'

export default {
  components: {
    ModuleHeader,
    StepsBlock,
    StepContainer
  },
  data () {
    return {
      id: this.$route.query.id,
      step: 0,
      contract: null,
      saving: false,
      saved: false,
      mode: 'normal',
      modes: creatingModes,
      isEdited: false,
      steps,
      archiveSteps
    }
  },
  computed: {
    filteredSteps () {
      if (this.mode !== 'archive') {
        return steps
      }

      return archiveSteps
    },
    editorState () {
      switch (this.mode) {
        case 'no-documents':
          return noDocumentsCheckFn(this.contract)
        case 'guarantee-letter':
          return guaranteeLetterCheckFn(this.contract)
        default:
          return fullCheckFn(this.contract)
      }
    }
  },
  watch: {
    contract: {
      handler: function () {
        if (!this.contract) return
        if (this.contract.id) return

        if (isEditedContract(this.contract)) {
          saveDataToSS('creatingContract', this.contract)
          this.$store.commit('SET_ACTIVE_TASK_STATE', {
            type: 'contract',
            value: true
          })
          this.isEdited = true
        }
      },
      deep: true,
      immediate: false
    }
  },
  methods: {
    changeStep (step) {
      this.step = step
    },
    getData () {
      const data = cloneDeep(this.contract)

      // complex attributes
      allComplexFields.forEach(([prop, idProp]) => {
        modifyComplexData(data, prop, idProp)
      })

      // modify roadmaps
      data.roadmaps = modifyRoadmapsDates(data.roadmaps)

      // check prepaid
      if (!data.prepaid || data.prepaid_amount === 0) {
        data.roadmaps.forEach((r) => this.$set(r, 'prepaid_value', 0))
      }
      // check retention
      if (!data.retention || data.retention_amount === 0) {
        data.roadmaps.forEach((r) => this.$set(r, 'retention_value', 0))
      }

      // modify payment tasks
      if (data.payment_tasks.length) {
        data.payment_tasks = data.payment_tasks.map((t) => ({
          value: t.value,
          payment_date: t.payment_date
        }))
      }

      // modify sign date
      data.sign_date = moment(data.sign_date).format('YYYY-MM-DD')

      if (!data.id) delete data.id

      return data
    },
    async saveContract () {
      try {
        this.saving = true
        const url = `page/project`
        const data = this.getData()

        await this.$store.dispatch('POST_REQUEST', { url, data })
        sessionStorage.removeItem('creatingContract')
        this.$store.commit('SET_ACTIVE_TASK_STATE', {
          type: 'contract',
          value: false
        })
        this.$Message.success('Сохранение проекта выполнено успешно')
        this.saving = false
        this.saved = true
        this.$router.push(`/app/contracts/all`)
      } catch (error) {
        console.log(error)

        if (error.response) {
          const message =
          error.response.data[0] || 'Произошла ошибка при сохранении объекта'
          this.$Message.error(message)
          this.saving = false
        }
      }
    },
    getInitialContract () {
      return {
        ...commonInfo,
        ...subInfo,
        ...cloneDeep(roadMap),
        ...cloneDeep(documents)
      }
    },
    clearData () {
      sessionStorage.removeItem('creatingContract')
      this.$store.commit('SET_ACTIVE_TASK_STATE', {
        type: 'contract',
        value: false
      })
      this.contract = this.getInitialContract()
      this.isEdited = false
    },
    modeChangeHandler () {
      this.clearData()
      this.step = 0
    }
  },
  created () {
    this.contract = getDataFromSS(
      'creatingContract',
      this.getInitialContract()
    )
  }
}
</script>

<style lang="less">
.contract-editor {
  &__buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .module-header {
      margin-bottom: 0;
    }
  }

  &-mode {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .ivu-select {
      width: 220px;
    }

    .ms-help {
      flex-shrink: 0;
      margin-right: 8px;
    }
  }
}
</style>
